import { useState, useCallback, useEffect, useMemo, useContext, useRef } from 'react';
import { getRootFolder, getFolderById } from '@/api/folder';
import { createFolder as apiCreateFolder } from '@/api/folder';
import NotificationContext from '@/context/notification';
import type { BrowsableItem, FolderDestination, BreadcrumbItem } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawItem = Record<string, any>;

/** Item being moved/copied — may be a resource, folder, or project */
interface PickerSourceItem {
  id: string;
  itemtype: string;
}

interface UseFolderPickerOptions {
  useCase: 'move' | 'copy';
  selectedItems?: PickerSourceItem[];
  currentFolderId?: string;
  onSelectDestination: (dest: FolderDestination | undefined) => void;
  updateIsQuerying?: (querying: boolean) => void;
}

/** Cached folder data keyed by folder ID ('root' for the root) */
interface CachedFolder {
  folder: BrowsableItem | 'root';
  items: BrowsableItem[];
}

function toBrowsableItem(raw: RawItem): BrowsableItem | null {
  if (!raw || (raw.itemtype !== 'folder' && raw.itemtype !== 'project')) return null;

  let resources: BrowsableItem['resources'];
  if (raw.resources) {
    const r = raw.resources;
    resources = {
      folder: typeof r.folder === 'object' ? Number(r.folder?.N ?? 0) : Number(r.folder ?? 0),
      project: typeof r.project === 'object' ? Number(r.project?.N ?? 0) : Number(r.project ?? 0),
    };
  }

  let breadcrumb: BreadcrumbItem[] | undefined;
  if (Array.isArray(raw.breadcrumb)) {
    breadcrumb = raw.breadcrumb.map((b: RawItem) => ({
      id: b.id?.S ?? b.id,
      title: b.title?.S ?? b.title,
      itemtype: b.itemtype?.S ?? b.itemtype,
    }));
  }

  return {
    id: raw.id,
    title: raw.title,
    itemtype: raw.itemtype,
    resources,
    breadcrumb,
  };
}

export interface FolderPickerState {
  status: 'loading' | 'success' | 'error';
  items: BrowsableItem[];
  currentFolder: BrowsableItem | 'root';
  selectedItem: BrowsableItem | undefined;
  searchQuery: string;
  filteredItems: BrowsableItem[];
  focusedIndex: number;
  isCreatingFolder: boolean;

  navigateToRoot: () => void;
  navigateToFolder: (id: string) => void;
  selectItem: (item: BrowsableItem) => void;
  setSearchQuery: (q: string) => void;
  setFocusedIndex: (i: number) => void;
  setIsCreatingFolder: (v: boolean) => void;
  createFolder: (title: string) => Promise<void>;
  retry: () => void;
}

export function useFolderPicker({
  useCase,
  selectedItems,
  currentFolderId,
  onSelectDestination,
  updateIsQuerying,
}: UseFolderPickerOptions): FolderPickerState {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [items, setItems] = useState<BrowsableItem[]>([]);
  const [currentFolder, setCurrentFolder] = useState<BrowsableItem | 'root'>('root');
  const [selectedItem, setSelectedItem] = useState<BrowsableItem | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const { add } = useContext(NotificationContext);

  // Stable refs for callback props
  const onSelectDestinationRef = useRef(onSelectDestination);
  onSelectDestinationRef.current = onSelectDestination;
  const updateIsQueryingRef = useRef(updateIsQuerying);
  updateIsQueryingRef.current = updateIsQuerying;
  const addRef = useRef(add);
  addRef.current = add;

  // Folder cache — avoids re-fetching when navigating back
  const cacheRef = useRef<Map<string, CachedFolder>>(new Map());

  // ── Notify parent via effect (not during render) ───────────────
  const destinationToReport = selectedItem ?? (currentFolder === 'root' ? 'root' : currentFolder);

  useEffect(() => {
    if (status === 'success') {
      onSelectDestinationRef.current(
        destinationToReport === 'root' ? 'root' : destinationToReport,
      );
    }
  }, [destinationToReport, status]);

  // ── Fetch (with cache) ─────────────────────────────────────────
  const fetchFolder = useCallback(
    async (folderId?: string, bypassCache = false) => {
      const cacheKey = folderId ?? 'root';

      // Try cache first
      if (!bypassCache) {
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
          setCurrentFolder(cached.folder);
          setItems(cached.items);
          setSelectedItem(undefined);
          setSearchQuery('');
          setFocusedIndex(-1);
          setStatus('success');
          return;
        }
      }

      setStatus('loading');
      setSelectedItem(undefined);
      setSearchQuery('');
      setFocusedIndex(-1);
      updateIsQueryingRef.current?.(true);

      try {
        const response = folderId
          ? await getFolderById(folderId)
          : await getRootFolder();

        const folder: BrowsableItem | 'root' = response.folder
          ? (toBrowsableItem(response.folder as RawItem) ?? 'root')
          : 'root';

        const browsable = (response.data as RawItem[])
          .map(toBrowsableItem)
          .filter((item): item is BrowsableItem => item !== null);

        // Store in cache
        cacheRef.current.set(cacheKey, { folder, items: browsable });

        setCurrentFolder(folder);
        setItems(browsable);
        setStatus('success');
      } catch {
        setStatus('error');
        addRef.current({ id: Date.now(), type: 'error', message: 'Could not load folder' });
      } finally {
        updateIsQueryingRef.current?.(false);
      }
    },
    [],
  );

  // Fetch root on mount
  useEffect(() => {
    fetchFolder();
  }, [fetchFolder]);

  // ── Navigation ─────────────────────────────────────────────────
  const navigateToRoot = useCallback(() => {
    fetchFolder();
  }, [fetchFolder]);

  const navigateToFolder = useCallback(
    (id: string) => {
      fetchFolder(id);
    },
    [fetchFolder],
  );

  // ── Selection ──────────────────────────────────────────────────
  const selectItem = useCallback(
    (item: BrowsableItem) => {
      if (useCase === 'move' && selectedItems) {
        const isSelfMove = selectedItems.some((si) => si.id === item.id);
        if (isSelfMove) {
          addRef.current({ id: Date.now(), type: 'info', message: "Can't move folder inside itself" });
          return;
        }
      }

      setSelectedItem((prev) => (prev?.id === item.id ? undefined : item));
    },
    [useCase, selectedItems],
  );

  // ── Create folder ──────────────────────────────────────────────
  const createFolder = useCallback(
    async (title: string) => {
      const parentId = currentFolder === 'root' ? 'root' : currentFolder.id;
      updateIsQueryingRef.current?.(true);

      try {
        await apiCreateFolder(title, parentId);
        // Invalidate cache for this folder and re-fetch
        cacheRef.current.delete(parentId === 'root' ? 'root' : parentId);
        await fetchFolder(parentId === 'root' ? undefined : parentId);
        setIsCreatingFolder(false);
        addRef.current({ id: Date.now(), type: 'success', message: 'Folder created' });
      } catch {
        addRef.current({ id: Date.now(), type: 'error', message: 'Could not create folder' });
      } finally {
        updateIsQueryingRef.current?.(false);
      }
    },
    [currentFolder, fetchFolder],
  );

  // ── Filtering ──────────────────────────────────────────────────
  const movingNonResource =
    useCase === 'move' &&
    selectedItems?.some((si) => si.itemtype !== 'resource') === true;

  const filteredItems = useMemo(() => {
    let result = items;

    if (movingNonResource) {
      result = result.filter((item) => item.itemtype !== 'project');
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item) => item.title.toLowerCase().includes(q));
    }

    return result;
  }, [items, searchQuery, movingNonResource]);

  const retry = useCallback(() => {
    const id = currentFolder === 'root' ? undefined : currentFolder.id;
    // Bypass cache on retry
    fetchFolder(id, true);
  }, [currentFolder, fetchFolder]);

  return {
    status,
    items,
    currentFolder,
    selectedItem,
    searchQuery,
    filteredItems,
    focusedIndex,
    isCreatingFolder,
    navigateToRoot,
    navigateToFolder,
    selectItem,
    setSearchQuery,
    setFocusedIndex,
    setIsCreatingFolder,
    createFolder,
    retry,
  };
}
